import { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView, useReducedMotion } from 'framer-motion';

const rand = (a, b) => a + Math.random() * (b - a);

const makeBubble = (id, fresh = false) => ({
  id,
  key: `${id}-${Math.random().toString(36).slice(2, 8)}`,
  size: rand(18, 64),
  left: rand(2, 96),
  duration: rand(10, 18),
  // bolhas iniciais já começam no meio do caminho; as que renascem esperam um pouco
  delay: fresh ? rand(0.6, 2.4) : -rand(0, 16),
  sway: rand(-50, 50),
});

/**
 * Bolhas de sabão que sobem pela seção — clique/toque para estourar.
 * Referência direta às legendas "🧼🚿" das fotos pós-banho.
 */
export const Bubbles = ({ count = 10, travel = '120vh', className = '', onPop }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref);
  const [bubbles, setBubbles] = useState(() => Array.from({ length: count }, (_, i) => makeBubble(i)));
  const [bursts, setBursts] = useState([]);

  const pop = useCallback(
    (bubble, e) => {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) {
        const id = Math.random();
        setBursts((prev) => [...prev, { id, x: e.clientX - rect.left, y: e.clientY - rect.top, size: bubble.size }]);
        setTimeout(() => setBursts((prev) => prev.filter((b) => b.id !== id)), 700);
      }
      setBubbles((prev) => prev.map((b) => (b.id === bubble.id ? makeBubble(bubble.id, true) : b)));
      onPop?.();
    },
    [onPop]
  );

  if (reduce) return null;

  return (
    <div ref={ref} className={`absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
      {bubbles.map((b) => (
        <button
          key={b.key}
          type="button"
          tabIndex={-1}
          onPointerDown={(e) => pop(b, e)}
          data-cursor="Pop!"
          className="bubble absolute -bottom-24 cursor-pointer"
          style={{
            left: `${b.left}%`,
            width: b.size,
            height: b.size,
            '--sway': `${b.sway}px`,
            '--travel': travel,
            animation: `rise ${b.duration}s linear ${b.delay}s infinite`,
            animationPlayState: inView ? 'running' : 'paused',
          }}
        />
      ))}
      <AnimatePresence>
        {bursts.map((burst) => (
          <span key={burst.id} className="absolute pointer-events-none" style={{ left: burst.x, top: burst.y }}>
            {Array.from({ length: 8 }).map((_, i) => {
              const angle = (i / 8) * Math.PI * 2;
              const dist = burst.size * 0.9;
              return (
                <motion.span
                  key={i}
                  className="absolute block w-1.5 h-1.5 rounded-full bg-pet-blue/60"
                  initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                  animate={{ x: Math.cos(angle) * dist, y: Math.sin(angle) * dist, opacity: 0, scale: 0.4 }}
                  transition={{ duration: 0.55, ease: 'easeOut' }}
                />
              );
            })}
            <motion.span
              className="absolute block rounded-full border-2 border-white/80"
              style={{ width: burst.size, height: burst.size, left: -burst.size / 2, top: -burst.size / 2 }}
              initial={{ scale: 1, opacity: 0.9 }}
              animate={{ scale: 1.6, opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            />
          </span>
        ))}
      </AnimatePresence>
    </div>
  );
};

export default Bubbles;
